import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Building2, LogIn, Mail, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { usePreventScroll } from '@/hooks/usePreventScroll';
import { useNavigate } from 'react-router-dom';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegisterBusiness: () => void;
}

export function LoginModal({
  isOpen,
  onClose,
  onRegisterBusiness,
}: LoginModalProps) {
  usePreventScroll(isOpen);
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginType, setLoginType] = useState<'traveler' | 'business'>(
    'traveler'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Login attempt:', { email, loginType });
      const { user } = await login({ email, password });
      console.log('Login response user:', user);

      if (loginType === 'business' && user.role !== 'business' && user.role !== 'admin') {
        console.log('Role mismatch:', { userRole: user.role, loginType });
        toast.error(
          'This account is not registered as a business. Please use traveler login or register your business.'
        );
        return;
      }

      if (loginType === 'business') {
        console.log('Business login successful, navigating...');
        
        // Store business auth in separate storage for isolation
        const authData = localStorage.getItem('auth');
        if (authData) {
          localStorage.setItem('businessAuth', authData);
          console.log('Business auth stored separately');
        }
        
        toast.success('Welcome to your business dashboard!', {
          description: 'Redirecting you to manage your wildlife experiences...',
          duration: 2000,
        });
        setTimeout(() => {
          console.log('Executing navigation to /business');
          navigate('/business', { replace: true });
          onClose();
        }, 1000);
      } else {
        console.log('Traveler login successful');
        toast.success('Welcome back!', {
          description:
            'Get ready to explore amazing wildlife experiences.',
          duration: 2000,
        });
        onClose();
      }
    } catch (error) {
      console.error('Login error:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Invalid credentials';
      toast.error('Login failed', {
        description:
          message + '. Please check your credentials and try again.',
        duration: 4000,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 z-[50] bg-gradient-to-br from-white via-slate-50/50 to-white border-0 shadow-2xl">
        <div className="relative overflow-hidden rounded-lg">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-emerald-50/30 to-purple-50/20" />
          <div className="relative">
            <DialogHeader className="px-8 pt-8 pb-2">
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent text-center">
                Welcome Back
              </DialogTitle>
              <DialogDescription className="text-slate-600 text-center mt-2">
                Sign in to access your account and explore amazing wildlife experiences
              </DialogDescription>
            </DialogHeader>
            <div className="p-8">
              {/* Enhanced Tabs Component */}
              <Tabs
                value={loginType}
                onValueChange={(value) => {
                  console.log('Tabs value changed to:', value);
                  setLoginType(value as 'traveler' | 'business');
                }}
              >
                <TabsList className="grid w-full grid-cols-2 bg-white/60 backdrop-blur-sm border border-slate-200/50 p-1 rounded-xl shadow-sm">
                  <TabsTrigger 
                    value="traveler" 
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/25 rounded-lg transition-all duration-300"
                  >
                    <span className="flex items-center gap-2">
                      🌍 Traveler
                    </span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="business"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/25 rounded-lg transition-all duration-300"
                  >
                    <span className="flex items-center gap-2">
                      🏢 Business
                    </span>
                  </TabsTrigger>
                </TabsList>
                
                <div className="mt-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={loginType}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <form onSubmit={handleSubmit}>
                        <div className="space-y-5">
                          {/* Email Field */}
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="pl-10 bg-white/80 backdrop-blur-sm border-slate-200 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl h-12 transition-all duration-300 shadow-sm focus:shadow-md"
                                placeholder={`Enter your ${loginType} email`}
                                required
                              />
                            </div>
                          </div>

                          {/* Password Field */}
                          <div className="space-y-2">
                            <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="pl-10 bg-white/80 backdrop-blur-sm border-slate-200 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl h-12 transition-all duration-300 shadow-sm focus:shadow-md"
                                placeholder="Enter your password"
                                required
                              />
                            </div>
                          </div>

                          {/* Submit Button */}
                          <Button
                            type="submit"
                            className={`w-full h-12 rounded-xl font-medium transition-all duration-300 shadow-lg border-0 ${
                              loginType === 'traveler'
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 hover:shadow-blue-500/25'
                                : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-emerald-500/25'
                            }`}
                            disabled={isLoading}
                          >
                            <LogIn className="mr-2 h-4 w-4" />
                            {isLoading ? 'Signing in...' : 'Sign In'}
                          </Button>

                          {/* Business Registration Section */}
                          {loginType === 'business' && (
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 }}
                              className="pt-4 text-center"
                            >
                              <div className="text-sm text-slate-500 mb-3">
                                Don't have a business account?
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full border-emerald-300 text-emerald-600 hover:bg-gradient-to-r hover:from-emerald-500 hover:to-emerald-600 hover:text-white hover:border-transparent backdrop-blur-sm bg-white/60 rounded-xl h-11 transition-all duration-300 shadow-sm hover:shadow-emerald-500/20"
                                onClick={onRegisterBusiness}
                              >
                                <Building2 className="mr-2 h-4 w-4" />
                                Register Your Business
                              </Button>
                            </motion.div>
                          )}
                        </div>
                      </form>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}